import classNames from 'classnames';
import isString from 'lodash-es/isString';
import { ForwardedRef, ReactNode, useState } from 'react';

import useBackgroundIsDark from '../../hooks/useBackgroundIsDark';

import CollectionsList, { CollectionsListProps } from '../lists/CollectionsList';
import Background from '../partials/Background';

import styles from '../../styles/blocks/collections-row-block.module.css';

export interface CollectionsRowBlockProps extends CollectionsListProps {
    title?: ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    background?: Background;
    count?: number;
    withShadow?: boolean | null;
    withBorderRadius?: boolean;
    className?: string | null;
    titleClassName?: string | null;
    innerClassName?: string | null;
    listClassName?: string | null;
    itemsClassName?: string | null;
    backgroundClassName?: string | null;
    onEmpty?: () => void;
    onFilled?: () => void;
    onLoaded?: (loadedItems: Collection[]) => void;
}

function CollectionsRowBlock({
    title = null,
    header = null,
    footer = null,
    background = null,
    count = 12,
    withShadow = null,
    withBorderRadius = false,
    className = null,
    titleClassName = null,
    innerClassName = null,
    listClassName = null,
    itemsClassName = null,
    backgroundClassName = null,
    onEmpty = null,
    onFilled = null,
    onLoaded = null,
    ref: containerRef = null,
    ...props
}: CollectionsRowBlockProps & { ref?: ForwardedRef<HTMLDivElement> | null }) {
    const finalBackground = isString(background) ? { color: background } : background;
    const backgroundIsDark = useBackgroundIsDark(finalBackground);

    const [items, setItems] = useState<Collection[] | null>(null);
    const onListLoaded = (loadedItems: Collection[]) => {
        setItems(loadedItems);
        if (loadedItems.length === 0 && onEmpty !== null) {
            onEmpty();
        } else if (loadedItems.length > 0 && onFilled !== null) {
            onFilled();
        }
        if (onLoaded !== null) {
            onLoaded(loadedItems);
        }
    };

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.textIsWhite]: backgroundIsDark,
                    [styles.withBorderRadius]: withBorderRadius,
                    [styles.withShadow]: withShadow,
                    [styles.isEmpty]: items !== null && items.length === 0,
                },
                className,
            ])}
            ref={containerRef}
        >
            <div className={classNames([styles.inner, innerClassName])}>
                {finalBackground !== null ? (
                    <Background
                        {...finalBackground}
                        className={classNames([styles.background, backgroundClassName])}
                    />
                ) : null}

                {header ||
                    (title !== null ? (
                        <div className={classNames([styles.title, titleClassName])}>{title}</div>
                    ) : null)}
                <div className={classNames([styles.list, listClassName])}>
                    <CollectionsList
                        presentation="responsive"
                        cardWidth={150}
                        itemsClassName={classNames([
                            styles.items,
                            {
                                [itemsClassName]: itemsClassName !== null,
                            },
                        ])}
                        cardClassName={styles.card}
                        minColumns={2}
                        placeholderCount={count}
                        count={count}
                        onLoaded={onListLoaded}
                        {...props}
                    />
                </div>
                {footer}
            </div>
        </div>
    );
}

export default CollectionsRowBlock;
